/**
 * Tooltip layer.
 *
 * Hover on desktop, long-press on touch. UI_UX section 8 is binding here:
 * nothing essential may require hover, so a tooltip only ever explains
 * something that is also discoverable another way (a keyword links to HELP, an
 * unaffordable card also dims).
 *
 * One fixed-position layer renders whichever tooltip is active, so tooltips are
 * never clipped by a scrolling row and never stack.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import styles from './Tooltip.module.css';

interface TooltipState {
  content: ReactNode;
  x: number;
  y: number;
}

interface TooltipApi {
  show(content: ReactNode, anchor: DOMRect): void;
  hide(): void;
}

const TooltipContext = createContext<TooltipApi | null>(null);

const LONG_PRESS_MS = 450;
const HOVER_DELAY_MS = 260;

export function TooltipProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TooltipState | null>(null);

  const show = useCallback((content: ReactNode, anchor: DOMRect) => {
    setState({ content, x: anchor.left + anchor.width / 2, y: anchor.top });
  }, []);

  const hide = useCallback(() => setState(null), []);

  const api = useMemo<TooltipApi>(() => ({ show, hide }), [show, hide]);

  useEffect(() => {
    if (!state) return;
    const onScroll = () => setState(null);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [state]);

  return (
    <TooltipContext.Provider value={api}>
      {children}
      {state ? (
        <div
          className={styles.layer}
          style={{ left: state.x, top: state.y }}
          role="tooltip"
          aria-live="polite"
        >
          <div className={styles.bubble}>{state.content}</div>
        </div>
      ) : null}
    </TooltipContext.Provider>
  );
}

const NOOP: TooltipApi = { show: () => {}, hide: () => {} };

function useTooltipApi(): TooltipApi {
  return useContext(TooltipContext) ?? NOOP;
}

/**
 * Props to spread onto the element a tooltip describes. Returns pointer
 * handlers that do the right thing for both input types: a delayed hover on a
 * mouse, a long press on a finger. A long press also suppresses the click that
 * would otherwise follow.
 */
export function useTooltip(content: ReactNode, enabled = true) {
  const api = useTooltipApi();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressClick = useRef(false);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => clear, [clear]);

  const open = useCallback(
    (el: HTMLElement, delay: number, touch: boolean) => {
      clear();
      timer.current = setTimeout(() => {
        if (touch) suppressClick.current = true;
        api.show(content, el.getBoundingClientRect());
      }, delay);
    },
    [api, clear, content],
  );

  const close = useCallback(() => {
    clear();
    api.hide();
  }, [api, clear]);

  if (!enabled || content === null || content === undefined) {
    return {} as Record<string, never>;
  }

  return {
    onPointerEnter: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.pointerType === 'touch') return;
      open(e.currentTarget, HOVER_DELAY_MS, false);
    },
    onPointerLeave: close,
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.pointerType !== 'touch') return;
      open(e.currentTarget, LONG_PRESS_MS, true);
    },
    onPointerUp: close,
    onPointerCancel: close,
    onClickCapture: (e: ReactMouseEvent<HTMLElement>) => {
      if (suppressClick.current) {
        suppressClick.current = false;
        e.preventDefault();
        e.stopPropagation();
      }
    },
  };
}
